import React, { Component, createRef } from "react";
import styled, { css } from "styled-components";

import { ds } from "js/core/models/dataService";
import { _ } from "js/vendor";
import { app } from "js/namespaces";
import ErrorHandler from "js/core/utilities/errorHandler";
import { checkSlideCount } from "js/core/models/presentation";
import { ShowConfirmationDialog, ShowConvertedToClassicDialog, ShowDialog, ShowUpgradeDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { SlideTemplateInfoPage } from "js/react/views/SlideTemplateInfo/slideTemplateInfoPage";
import { AddSlideController } from "js/react/views/AddSlide/AddSlideController";
import { getSlideMenu } from "js/editor/editorMenu";
import { AddSlideContainer } from "js/react/views/AddSlide";
import HoldButton from "js/editor/buttons/HoldButton";
import { SlideLockType } from "common/constants";
import { IconMenu } from "./UiComponents";
import { Icon, MenuItem } from "@material-ui/core";
import { themeColors } from "js/react/sharedStyles";
import { Divider } from "js/react/components/UiComponents";
import { NestedMenuItem } from "./NestedMenuItem";
import { handleEditSharedSlide } from "js/editor/editShareSlide";
import { SwitchTemplateDialog } from "js/editor/TemplateSwitcher/SwitchTemplateDialog";
import { trackActivity } from "js/core/utilities/utilities";
import { FeatureType } from "common/features";
import { UpgradePlanDialogType } from "../views/MarketingDialogs/UpgradePlanDialog";
import { TeamSlideDialog } from "js/editor/TeamResources/TeamSlideDialog";
import { CreateSharedSlideDialog } from "../views/UserOptions/dialogs/CreateSharedSlideDialog";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { UIController } from "js/editor/dialogs/UIController";
import { noop } from "lodash-es";
import { linkedSlides } from "js/core/utilities/conversions";
import ProBadge from "./ProBadge";

const MenuItemCss = css`
    &&& {
        font-weight: 600;
        font-size: 13px;
        text-align: left;
        white-space: pre;
        padding: 15px 20px 15px 15px;
        cursor: pointer;
        display: flex;
        align-items: center;
        color: #444;

        :hover {
            background: #50bbe6;
            color: #fff;
        }
    }
`;

const StyledNestedMenuItem = styled(NestedMenuItem)`
    ${MenuItemCss}   
`;

const StyledMenuItem = styled(MenuItem)`
    ${MenuItemCss}    
`;

const StyledIcon = styled(Icon)`
    &&& {
        vertical-align: -6px;
        font-size: 20px;
        color: #777;
        margin-right: 6px;
    }
`;

export default class SlideOptions extends Component {
    state = {
        menuItems: [],
        isMenuOpen: false,
        isDeleteDisabled: false,
        isMoreOptionsDisabled: false
    }
    constructor(props) {
        super(props);

        this.$delete = createRef();
        this.$moreOptions = createRef();
    }

    setMenuOpen = value => this.setState({ isMenuOpen: value })

    componentDidMount() {
        app.mainView.editorView.lockModel.on("change:lockState", this.onLockSlideChange);

        app.mainView.editorView.once("editor-ready", () => {
            this.setState({
                menuItems: getSlideMenu({ source: "editor:slideOptions" })?.items ?? []
            });
        });
    }

    ComponentWillUnmount() {
        app.mainView.editorView.lockModel.off("change:lockState", this.onLockSlideChange);
    }

    onLockSlideChange = model => {
        if (model.attributes.lockState[SlideLockType.COLLABORATION]) {
            this.setState({
                isDeleteDisabled: true,
                isMoreOptionsDisabled: true
            });
            return;
        } else {
            this.setState({
                isDeleteDisabled: false,
                isMoreOptionsDisabled: false
            });
        }

        const orgOrTeamMember = (model.attributes.lockState[SlideLockType.ORG] || model.attributes.lockState[SlideLockType.TEAM_MEMBER]) &&
            (!app.user.has("teams") || !ds.selection.slide.isLibrarySlideInCurrentUserOrg());

        this.setState({ isDeleteDisabled: orgOrTeamMember });
    }

    showTemplateInfo =() => {
        ShowDialog(SlideTemplateInfoPage, {
            templateId: ds.selection.slide.get("template_id"), isLibrary: false, onCopySlide: slideId => {
                console.log(slideId);
                AddSlideController.replaceSlideInPresentation(slideId, ds.selection.slide);
            }
        });
    }

    addSlide = () => {
        checkSlideCount(ds.presentations, 1, ds.selection.presentation.getWorkspaceId()).then(() => {
            ShowDialog(AddSlideContainer);
        }).catch(err => {
            ErrorHandler.handleSlideLimitReached(err, { workspaceId: ds.selection.presentation.getWorkspaceId() });
        });
    }

    duplicateSlide = () => {
        app.mainView.editorView.duplicateSlide(ds.selection.slide);
    }

    deleteSlide =() => {
        app.mainView.editorView.deleteSlide(ds.selection.slide);
    }

    callback = async action => {
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        const isLibrarySlide = ds.selection.slide.has("libraryItemId");
        const isReadOnly = (ds.selection.slide.isLibrarySlide() && app.mainView.editorView.$lockedSlideShield);
        switch (action) {
            case "update-slide":
                app.mainView.editorView.reloadCurrentSlide(true, true);
                break;
            case "switch-template":
                if (isReadOnly) {
                    handleEditSharedSlide;
                } else {
                    trackActivity("Slide", "ShowSwitchTemplate", null, null, { source: "editor:slideOptions" }, { audit: false });
                    ShowDialog(SwitchTemplateDialog, {
                        canvas: app.currentCanvas
                    });
                }
                break;
            case "convert-to-authoring":
                if (!app.user.features.isFeatureEnabled(FeatureType.CONVERT_TO_CLASSIC, workspaceId)) {
                    ShowUpgradeDialog({
                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                        analytics: { cta: "ConvertToAuthoring", ...ds.selection.presentation.getAnalytics() },
                        workspaceId: ds.selection.presentation.getWorkspaceId()
                    });
                    return;
                }

                if (isReadOnly) {
                    handleEditSharedSlide()
                        .then(async canEdit => {
                            if (canEdit) {
                                await app.currentCanvas.convertToAuthoring();
                                if (!app.user.get("messagesSeen")?.showConvertedToClassicDialog) ShowConvertedToClassicDialog();
                            }
                        });
                } else {
                    await app.currentCanvas.convertToAuthoring();
                    if (!app.user.get("messagesSeen")?.showConvertedToClassicDialog) ShowConvertedToClassicDialog();
                }
                break;
            case "shared_slide_properties":
                ShowDialog(TeamSlideDialog, {
                    libraryItemId: ds.selection.slide.get("libraryItemId"),
                    openFromLibrary: false
                });
                break;
            case "skip":
                ds.selection.presentation.skipSlide(ds.selection.slide, true);
                app.currentCanvas.refreshCanvas();
                app.mainView.editorView.updateCurrentCanvasUI();
                break;
            case "unskip":
                ds.selection.presentation.skipSlide(ds.selection.slide, false);
                app.currentCanvas.refreshCanvas();
                app.mainView.editorView.updateCurrentCanvasUI();
                break;
            case "add_to_slide_library":
                if (isLibrarySlide) {
                    ShowWarningDialog({
                        title: "Failed to add slide to library",
                        message: "This slide is already part of another library",
                    });
                    return;
                }

                // check for linked data which may change the dialogs that
                // are shown for the user
                const containsLinkedData = linkedSlides.detect(ds.selection.slide);
                const onBeforeSaveSlide = containsLinkedData
                    ? slide => {
                        linkedSlides.remove(slide);
                        slide.commit();
                    }
                    : null;

                // handles displaying the Share Dialog - possibly may be called
                // immediately or after showing a warning about linked slides
                const showShareSlideDialog = () => {
                    ShowDialog(CreateSharedSlideDialog, {
                        slide: ds.selection.slide,
                        presentation: ds.selection.presentation,
                        onBeforeSaveSlide
                    });
                };

                // since there's linked data, show a dialog warning that
                // the linked slides will be unlinked
                if (containsLinkedData) {
                    return ShowConfirmationDialog({
                        title: "This slide is linked to other parts of your deck.",
                        message: "Sharing it with your team will remove those links. Would you like to continue?",
                        acceptCallback: showShareSlideDialog,
                        cancelCallback: noop
                    });
                }

                // there's nothing to warn about so immediately
                // display the share dialog
                showShareSlideDialog();
                break;
            case "add_to_slide_library_cta":
                const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, workspaceId);
                const currentPlan = userIsBasicInWorkspace ? "basic" : app.user.analyticsPersonalPlan;
                window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
                break;
            case "version_history":
                if (isLibrarySlide && !app.mainView.editorView.isSlideEditor) {
                    handleEditSharedSlide();
                } else {
                    UIController.showVersionHistory();
                }
                break;
            case "delete_slide":
                app.mainView.editorView.deleteSlide(ds.selection.slide);
                break;
        }
    }

    render() {
        const { isMenuOpen, isMoreOptionsDisabled, menuItems } = this.state;
        const deleteAction = !ds.selection.slide || !ds.selection.slide.get("libraryItemId") ? "Delete" : "Remove";

        const options = [{
            className: "add_slide button",
            onClick: () => this.addSlide(),
            icon: "add_circle",
            label: "Add Slide"
        }, {
            className: "duplicate_slide button",
            onClick: ()=> this.duplicateSlide(),
            icon: "content_copy",
            label: "Duplicate"
        }];

        return (<>
            <div className="spacer"></div>
            {options.map(({ className, onClick, icon, label }, index) => (
                <div className={className} onClick={onClick} key={index}>
                    <i className="micon">{icon}</i>
                    <label>{label}</label>
                </div>
            ))}
            {<IconMenu
                disabled={isMoreOptionsDisabled}
                $menuIsOpen={isMenuOpen}
                $onMenuClick={() => this.setMenuOpen(true)}
                $onMenuClose={() => this.setMenuOpen(false)}
                icon="more_horiz"
                buttonLabel="More"
                classes={{
                    root: "more_options",
                    label: "button"
                }}
                transformOrigin = {{
                    vertical: "bottom",
                    horizontal: "center"
                }}
                $colorOnHover={`${themeColors.ui_blue}`}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}>
                {menuItems.map(({ type, label, icon, menu, value, showProBadge, upgradeType, proBadgeAnalytics, proBadgeWorkspaceId }, index) => {
                    switch (type) {
                        case "divider":
                            return (<Divider key={index} margin={5} />);
                        case "menu":
                            return (<StyledNestedMenuItem icon={icon && <StyledIcon>{icon}</StyledIcon>} label={label} key={index}>
                                {menu.items.map(({ label, icon, value, type, enabled = true }, index)=>(
                                    type === "divider" ? (<Divider margin={5} key={index}/>)
                                        : <StyledMenuItem
                                            disabled={!enabled} key={index} onMouseDown={() => menu.callback(value)}>
                                            {icon && <StyledIcon>{icon}</StyledIcon>}
                                            {label}
                                        </StyledMenuItem>))}
                            </StyledNestedMenuItem>);
                        default:
                            return (
                                <StyledMenuItem
                                    key={index}
                                    onClick={() => this.callback(value)}>
                                    {icon && <StyledIcon>{icon}</StyledIcon>}
                                    {label}
                                    {showProBadge && <ProBadge
                                        show={showProBadge}
                                        upgradeType={upgradeType}
                                        analytics={proBadgeAnalytics}
                                        workspaceId={proBadgeWorkspaceId}
                                    />}
                                </StyledMenuItem>
                            );
                    }
                })}
            </IconMenu>}
            <HoldButton icon="delete"
                additionalClass={`delete_slide ${this.state.isDeleteDisabled ? "disabled" : ""}`}
                label={!ds.selection.slide || !ds.selection.slide.get("libraryItemId") ? "Delete" : "Remove"}
                holdLabel={`Hold to ${deleteAction}`}
                callback={() => this.deleteSlide()}
            />
        </>
        );
    }
}

